import axios from 'axios';
import { ENDPOINTS, CONFIG, STORAGE_KEYS } from '../utils/constants';
import { emitUnauthorized } from '../utils/authEvents';

// Create Axios instance with base configuration
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000/api',
  timeout: CONFIG.API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token refresh state management (prevents race conditions)
let isRefreshing = false;
let refreshSubscribers = [];

/**
 * Add request to queue while token is being refreshed
 * @param {Function} callback - Callback to execute after token refresh
 */
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

/**
 * Execute all queued requests with new token
 * @param {string} token - New access token
 */
const onTokenRefreshed = (token) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

/**
 * Clear auth data from localStorage
 */
const clearAuthData = () => {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER);
  localStorage.removeItem(STORAGE_KEYS.PROFILE);
};

// Request interceptor - attach JWT access token from localStorage
api.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 errors and refresh tokens automatically
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }

      const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

      if (!refreshToken) {
        // No refresh token available, emit unauthorized event
        clearAuthData();
        emitUnauthorized();
        return Promise.reject(error);
      }

      isRefreshing = true;

      try {
        // Attempt to refresh the access token
        const response = await axios.post(
          `${import.meta.env.VITE_API_URL || 'http://localhost:8000/api'}${ENDPOINTS.AUTH.TOKEN_REFRESH}`,
          { refresh: refreshToken },
          { timeout: CONFIG.API_TIMEOUT }
        );

        const { access } = response.data;

        // Store new access token
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access);

        // Update the authorization header with new token
        originalRequest.headers.Authorization = `Bearer ${access}`;

        // Notify all queued requests
        onTokenRefreshed(access);

        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh token failed, clear storage and emit unauthorized event
        clearAuthData();
        emitUnauthorized();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
